import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Input,
  Row,
  Col,
  message,
  Typography,
  Statistic,
  Progress,
  Badge,
  Timeline,
  InputNumber,
  Popconfirm,
  Table,
  Tooltip
} from 'antd';
import {
  ScanOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  WarningOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Modal } from 'antd';
import moment from 'moment';
import type { 
  PackingOrder,
  PackingOrderItem,
  PackingScanResult
} from '../../types/packing';
import apiService from '../../../../services/apiService';
import { printWaybillSilently, printHktvMallWaybillSilently, printFedExLabelSilently } from '@utils/pdfUtils.ts';
import { playErrorSound } from '../../../../utils/soundUtils';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

interface PackingScanInterfaceProps {
  packingOrder: PackingOrder | null;
  onUpdatePackingOrder: (updated: PackingOrder) => void;
  onClose?: () => void;
}

const PackingScanInterface: React.FC<PackingScanInterfaceProps> = ({
  packingOrder,
  onUpdatePackingOrder,
  onClose
}) => {
  const { t } = useTranslation();
  // mark prop as read to satisfy linter
  void onClose;
  // 创建空订单作为默认值
  const emptyOrder: PackingOrder = {
    id: '',
    outboundOrderId: '',
    orderNumber: '',
    waybillId: '',
    waybillNumber: '',
    status: 'pending',
    packingZoneId: '',
    packingZoneName: '',
    pickupZoneId: '',
    pickupZoneName: '',
    packageType: '',
    packageWeight: 0,
    packageDimensions: '',
    packageValue: 0,
    items: [],
    totalItems: 0,
    totalQuantity: 0,
    customerName: '',
    customerContact: '',
    deliveryAddress: '',
    receiverName: '',
    receiverContact: '',
    deliveryProvider: '',
    fedexTrackingNo: '',
    discrepancies: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  
  const [currentOrder, setCurrentOrder] = useState<PackingOrder>(packingOrder || emptyOrder);
  const [scanMode, setScanMode] = useState<'waybill' | 'packing' | 'verification'>('waybill');
  const [scanning, setScanning] = useState(true);
  const [, setCurrentItem] = useState<PackingOrderItem | null>(null);
  const [scanHistory, setScanHistory] = useState<PackingScanResult[]>([]);
  
  
  // Scanner detection
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastScanTime, setLastScanTime] = useState(0);
  const [isRealScannerMode, setIsRealScannerMode] = useState(false);
  
  const scanInputRef = useRef<any>(null);
  // 移除未使用的表單實例
  // 統一的掃描輸入值
  const [scanInputValue, setScanInputValue] = useState('');
  // 避免重複觸發 PDF 下載
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  // 與揀貨一致的條碼/SKU歸一化
  const normalize = (v?: string | null) => String(v ?? '').trim();
  /*const normalize = (v?: string | null) =>
    String(v ?? '')
      .replace(/\s+/g, '')
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/[^0-9A-Za-z]/g, '');*/

  // 一次性：當所有產品已掃描=預期時，將出庫單狀態設為「已包裝」（PACKED）
  // FIX: Use tracking number (unique for split orders) to identify the specific order
  const didMarkPackedRef = useRef<boolean>(false);
  const ensureOutboundPackedOnce = async (trackingNumber?: string) => {
    if (didMarkPackedRef.current) return;
    // Use tracking number first (unique for split orders), fallback to outboundOrderId, then orderNumber
    const identifier = String(trackingNumber || currentOrder.trackingNumber || currentOrder.outboundOrderId || currentOrder.orderNumber || '').trim();
    if (!identifier) { didMarkPackedRef.current = true; return; }
    try {
      await apiService.updateOutboundOrder(identifier, { status: 'PACKED' });
    } catch {
      // 忽略錯誤，不影響畫面流程
    } finally {
      didMarkPackedRef.current = true;
    }
  };

  // 將後端錯誤轉換為友善中文文案
  const getFriendlyErrorMessage = (err: any, context: 'waybill' | 'product' | 'verification'): string => {
    const raw = err?.response?.data?.message || err?.message || String(err ?? '');
    const lower = String(raw).toLowerCase();

    if (context === 'waybill') {
      const is404 = err?.response?.status === 404 || lower.includes('404');
      const mentionsNotExist = lower.includes('not exist') || lower.includes('not found');
      const mentionsWaybill = lower.includes('waybill');
      if (is404 || (mentionsWaybill && mentionsNotExist)) {
        return t('未找到此運單的產品');
      }
    }

    return raw || t('系統錯誤');
  };

  // 自動打印並生成 PDF（僅在未下載過時觸發）
  // 使用靜默打印，不打開新視窗
  const attemptGeneratePdf = async (order: PackingOrder) => {
    const orderNo = order.orderNumber
    const waybillNo = order.waybillNumber;
    const outboundOrderId = order.outboundOrderId;
    const deliveryProvider = order.deliveryProvider || '';
    const fedexTrackingNo = order.fedexTrackingNo || '';

    console.log('[PackingScan] attemptGeneratePdf 開始執行', {
      orderNo,
      waybillNo,
      outboundOrderId,
      deliveryProvider,
      fedexTrackingNo,
      pdfDownloaded
    });

    if (pdfDownloaded) {
      console.log('[PackingScan] pdfDownloaded=true，跳過打印');
      return;
    }

    try {
      // Check if this is a FedEx order
      const isFedExOrder = deliveryProvider.toLowerCase() === 'fedex' ||
                           deliveryProvider.toLowerCase().includes('fedex') ||
                           !!fedexTrackingNo;

      console.log('[PackingScan] isFedExOrder:', isFedExOrder);

      if (isFedExOrder && outboundOrderId) {
        // FedEx order: print FedEx label (4x6 thermal format)
        console.log(`[PackingScan] 偵測到 FedEx 訂單，使用 FedEx 標籤打印: ${outboundOrderId}`);
        await printFedExLabelSilently(outboundOrderId, fedexTrackingNo || waybillNo);
        console.log('[PackingScan] FedEx 標籤打印完成');
      } else if (waybillNo && orderNo) {
        // Non-FedEx order: use standard waybill printing
        console.log('[PackingScan] 使用標準運單打印');
        if (String(orderNo).includes('H')) {
          await printHktvMallWaybillSilently(orderNo, waybillNo);
        } else {
          await printWaybillSilently(orderNo, waybillNo);
        }
        console.log('[PackingScan] 標準運單打印完成');
      } else {
        console.log('[PackingScan] 無運單號或訂單號，跳過打印');
        return;
      }

      setPdfDownloaded(true);
      console.log('[PackingScan] setPdfDownloaded(true)');
    } catch (pdfError) {
      console.error('[PackingScan] 打印和PDF生成失敗:', pdfError);
      // Error message is already handled by the silent print functions
    }
  };

  const handleClearScanHistory = () => {
    setScanHistory([]);
    message.success(t('已清空掃描歷史'));
  };

  useEffect(() => {
    // Focus on scan input when component mounts or scan mode changes
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [scanMode]);

  // Hardware barcode scanner integration
  useEffect(() => {
    let scannerTimeout: NodeJS.Timeout;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const currentTime = Date.now();
      
      // Check if this could be from a barcode scanner (rapid input)
      if (currentTime - lastScanTime < 100) {
        setIsRealScannerMode(true);
      }
      
      setLastScanTime(currentTime);

      // Handle Enter key (scanner suffix)
      if (event.key === 'Enter') {
        event.preventDefault();
        if (scanBuffer.trim().length > 0) {
          handleScan(scanBuffer.trim());
          setScanBuffer('');
          setIsRealScannerMode(false);
        }
        return;
      }

      // Handle regular characters (build barcode)
      if (event.key.length === 1) {
        event.preventDefault();
        setScanBuffer(prev => prev + event.key);
        
        // Clear buffer after delay (in case scanner doesn't send Enter)
        clearTimeout(scannerTimeout);
        scannerTimeout = setTimeout(() => {
          if (scanBuffer.length > 8) { // Minimum barcode length
            handleScan(scanBuffer.trim());
          }
          setScanBuffer('');
          setIsRealScannerMode(false);
        }, 300);
      }
    };

    // Add global keyboard listener for barcode scanner
    if (scanning) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(scannerTimeout);
    };
  }, [scanning, scanBuffer, lastScanTime]);

  // Visual feedback for scanner detection
  useEffect(() => {
    if (isRealScannerMode && scanBuffer.length > 0) {
      // Show visual feedback that scanner is detected
      const indicator = document.createElement('div');
      indicator.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #52c41a;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        z-index: 9999;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;
      indicator.textContent = t('🔍 掃描中... {{scanBuffer}}', { scanBuffer });
      document.body.appendChild(indicator);
      
      setTimeout(() => {
        if (document.body.contains(indicator)) {
          document.body.removeChild(indicator);
        }
      }, 2000);
    }
  }, [isRealScannerMode, scanBuffer]);

  const handleWaybillScan = async (barcode: string) => {
    try {
      const searchTerm = barcode.trim();

      // 清空之前的掃描歷史，並重置所有狀態，開始新的包裝訂單
      setScanHistory([]);
      setCurrentOrder(emptyOrder);  // 重置當前訂單，避免舊訂單的items影響新訂單
      setPdfDownloaded(false);      // 重置 PDF 下載狀態
      setCurrentItem(null);         // 清空當前項目
      didMarkPackedRef.current = false;  // 重置出庫狀態標記

      // 第一步：尝试从包装订单列表中查找（包含展開的bundle items）
      try {
        const packingResp: any = await apiService.getPackingOrders({ search: searchTerm, limit: 10 });
        const packingOrders = packingResp?.data || packingResp?.packings || [];

        // 查找匹配的订单（运单号或订单号或tracking number）
        const matchedOrder = packingOrders.find((po: any) =>
          po.waybillNumber === searchTerm ||
          po.orderNumber === searchTerm ||
          po.packingNumber === searchTerm ||
          po.trackingNumber === searchTerm
        );

        if (matchedOrder) {
          // 檢查出庫訂單狀態 - 只有「核對中」或「包裝中」狀態才能進行包裝掃描
          // 只使用 outbound order 的 outboundStatus，不檢查 packing order status
          const orderStatus = String(matchedOrder.outboundStatus || '').toLowerCase();
          const allowedStatuses = ['verifying', 'checking', 'packing'];

          if (!allowedStatuses.includes(orderStatus)) {
            // NOTE: keys here are ascii statuses (data), values are Chinese display
            // text. 處理中/配貨中/已發貨/已取消 are the persisted outbound-order status
            // values matched elsewhere by backend SQL — never wrapped in t() per the
            // glossary. The rest are plain display copy.
            const statusDisplayMap: Record<string, string> = {
              'pending': t('待處理'),
              'processing': '處理中',
              'allocating': '配貨中',
              'picking': t('揀貨中'),
              'packed': t('已打包'),
              'pickup_pending': t('待取貨'),
              'shipped': '已發貨',
              'delivered': t('已送達'),
              'cancelled': '已取消',
              'completed': t('已完成')
            };
            const statusText = statusDisplayMap[orderStatus] || orderStatus || t('未知');
            const orderLabel = matchedOrder.orderNumber || matchedOrder.packingNumber;

            playErrorSound();
            Modal.warning({
              title: t('無法進行包裝掃描'),
              content: t(
                '訂單狀態不符合要求，只有「核對中」或「包裝中」狀態的訂單才能進行包裝掃描。\n\n訂單 {{orderLabel}} 目前狀態: {{statusText}}',
                { orderLabel, statusText }
              ),
              okText: t('確定')
            });

            const scanResult: PackingScanResult = {
              barcode: searchTerm,
              type: 'waybill',
              success: false,
              error: t('訂單狀態不符: {{statusText}}，需要「核對中」或「包裝中」狀態', { statusText }),
              timestamp: new Date(),
              operatorId: 'current_user',
              operatorName: 'Current User'
            };
            setScanHistory([scanResult]);
            return null;
          }

          // 找到已存在的包装订单，先调用 startPacking 触发 bundle 展开
          try {
            await apiService.startPacking(matchedOrder.id, {});
          } catch (startErr) {
            // 忽略错误，可能已经在 packing 状态
            console.log('startPacking ignored:', startErr);
          }

          // 加载详情（包含展开后的 items）
          const detailResp: any = await apiService.getPackingOrderDetail(matchedOrder.id);
          const orderDetail = detailResp?.data || detailResp;

          if (orderDetail) {
            // 转换为 PackingOrder 格式
            const packingOrder: PackingOrder = {
              id: orderDetail.id,
              outboundOrderId: orderDetail.outboundOrderId || orderDetail.outbound_order_id || '',
              orderNumber: orderDetail.orderNumber || orderDetail.order_number || orderDetail.packingNumber || orderDetail.packing_number || '',
              waybillId: orderDetail.waybillId || orderDetail.waybill_id || '',
              waybillNumber: orderDetail.waybillNumber || orderDetail.waybill_number || orderDetail.trackingNumber || orderDetail.tracking_number || searchTerm,
              items: (orderDetail.items || []).map((item: any) => ({
                id: item.id,
                productId: item.productId || item.product_id,
                productSku: item.productSku || item.product_sku,
                productName: item.productName || item.product_name,
                productBarcode: item.productBarcode || item.product_barcode || item.productSku || item.product_sku,
                expectedQuantity: item.expectedQuantity || item.expected_quantity || item.quantity,
                scannedQuantity: item.scannedQuantity || item.scanned_quantity || 0,
                packedQuantity: item.packedQuantity || item.packed_quantity || 0,
                currentLocation: item.currentLocation || item.current_location,
                targetLocation: item.targetLocation || item.target_location,
                status: item.status || 'pending',
                hasDiscrepancy: item.hasDiscrepancy || item.has_discrepancy || false,
                lotNumber: item.lotNumber || item.lot_number,
                serialNumber: item.serialNumber || item.serial_number,
              })),
              status: orderDetail.status || 'packing',
              packingZoneId: orderDetail.packingZoneId || orderDetail.packing_zone_id || '',
              packingZoneName: orderDetail.packingZoneName || orderDetail.packing_zone_name || '',
              pickupZoneId: orderDetail.pickupZoneId || orderDetail.pickup_zone_id || '',
              pickupZoneName: orderDetail.pickupZoneName || orderDetail.pickup_zone_name || '',
              packageType: orderDetail.packageType || orderDetail.package_type || '',
              packageWeight: orderDetail.packageWeight || orderDetail.package_weight || 0,
              packageDimensions: orderDetail.packageDimensions || orderDetail.package_dimensions || '',
              packageValue: orderDetail.packageValue || orderDetail.package_value || 0,
              totalItems: orderDetail.totalItems || orderDetail.total_items || (orderDetail.items || []).length,
              totalQuantity: orderDetail.totalQuantity || orderDetail.total_quantity || 0,
              customerName: orderDetail.customerName || orderDetail.customer_name || '',
              customerContact: orderDetail.customerContact || orderDetail.customer_contact || '',
              receiverName: orderDetail.receiverName || orderDetail.receiver_name || '',
              receiverContact: orderDetail.receiverContact || orderDetail.receiver_contact || '',
              deliveryAddress: orderDetail.deliveryAddress || orderDetail.delivery_address || '',
              deliveryProvider: orderDetail.deliveryProvider || orderDetail.delivery_provider || '',
              fedexTrackingNo: orderDetail.fedexTrackingNo || orderDetail.fedex_tracking_no || '',
              // FIX: Include trackingNumber for split order identification
              trackingNumber: orderDetail.trackingNumber || orderDetail.tracking_number || '',
              discrepancies: orderDetail.discrepancies || [],
              createdAt: orderDetail.createdAt || orderDetail.created_at || new Date(),
              updatedAt: new Date(),
            };

            const scanResult: PackingScanResult = {
              barcode: searchTerm,
              type: 'waybill',
              success: true,
              data: packingOrder,
              timestamp: new Date(),
              operatorId: 'current_user',
              operatorName: 'Current User'
            };
            setScanHistory([scanResult]);

            setCurrentOrder(packingOrder);
            onUpdatePackingOrder(packingOrder);
            setScanMode('packing');
            message.success(t('已載入包裝訂單: {{orderNumber}}', { orderNumber: packingOrder.orderNumber }));

            // 修改：掃描運單號後立即完成包裝（一次掃描完成）
            // 自動將所有產品標記為已掃描和已包裝
            return packingOrder; // 返回訂單供 handleScan 使用
          }
        }
      } catch (packingErr) {
        console.log('未找到包装订单，尝试从 HKTV 获取...', packingErr);
      }

      // 第二步：如果没找到包装订单，尝试从 HKTV 加载（保留原有逻辑，仅用于 HKTV 订单）
      const resp: any = await apiService.getHKTVOrderByTrackingId(searchTerm);
      const order = resp?.data || resp;
      if (!order) {
        throw new Error(t('找不到對應訂單'));
      }
      console.log('HKTV 訂單:====================', order);

      // 對於 HKTV 訂單，也需要檢查出庫訂單狀態
      // HKTV 訂單會有 outboundStatus 或需要從 outbound order 查詢
      const hktvOrderStatus = String(order.outboundStatus || order.status || '').toLowerCase();
      const hktvAllowedStatuses = ['verifying', 'checking', 'packing'];

      if (hktvOrderStatus && !hktvAllowedStatuses.includes(hktvOrderStatus)) {
        const statusDisplayMap: Record<string, string> = {
          'pending': t('待處理'),
          'processing': '處理中',
          'allocating': '配貨中',
          'picking': t('揀貨中'),
          'packed': t('已打包'),
          'pickup_pending': t('待取貨'),
          'shipped': '已發貨',
          'delivered': t('已送達'),
          'cancelled': '已取消',
          'completed': t('已完成')
        };
        const statusText = statusDisplayMap[hktvOrderStatus] || hktvOrderStatus || t('未知');
        const orderLabel = order.orderId || searchTerm;

        playErrorSound();
        Modal.warning({
          title: t('無法進行包裝掃描'),
          content: t(
            '訂單狀態不符合要求，只有「核對中」或「包裝中」狀態的訂單才能進行包裝掃描。\n\n訂單 {{orderLabel}} 目前狀態: {{statusText}}',
            { orderLabel, statusText }
          ),
          okText: t('確定')
        });

        const scanResult: PackingScanResult = {
          barcode: searchTerm,
          type: 'waybill',
          success: false,
          error: t('訂單狀態不符: {{statusText}}，需要「核對中」或「包裝中」狀態', { statusText }),
          timestamp: new Date(),
          operatorId: 'current_user',
          operatorName: 'Current User'
        };
        setScanHistory([scanResult]);
        return null;
      }

      // 將 HKTV 訂單條目映射為包裝項目列表
      const items = Array.isArray(order.consignmentEntries) ? order.consignmentEntries.map((e: any, idx: number) => ({
        id: String(e.id || idx + 1),
        productId: e.skuId,
        productSku: e.skuId,
        productName: e.skuNameZh,
        productBarcode: e.productBarcode,
        expectedQuantity: Number(e.quantity || 1),
        scannedQuantity: 0,
        packedQuantity: 0,
        currentLocation: currentOrder.packingZoneId,
        targetLocation: currentOrder.packingZoneId,
        status: 'pending' as const,
        hasDiscrepancy: false,
      })) : [];

      const updatedOrder = {
        ...currentOrder,
        waybillNumber: searchTerm,
        // FIX: Include trackingNumber for split order identification
        trackingNumber: order.trackingNumber || order.tracking_number || searchTerm,
        orderNumber: order.orderId || currentOrder.orderNumber,
        receiverName: order.recipientName || currentOrder.receiverName,
        deliveryAddress: order.deliveryAddress || currentOrder.deliveryAddress,
        items,
        status: 'packing' as const,
        updatedAt: new Date(),
      };

      const scanResult: PackingScanResult = {
        barcode: searchTerm,
        type: 'waybill',
        success: true,
        data: updatedOrder,
        timestamp: new Date(),
        operatorId: 'current_user',
        operatorName: 'Current User'
      };
      setScanHistory([scanResult]);

      setCurrentOrder(updatedOrder);
      onUpdatePackingOrder(updatedOrder);
      setScanMode('packing');
      message.success(t('已載入 HKTV 訂單與產品'));

      // 返回訂單供 handleScan 使用
      return updatedOrder;
    } catch (err: any) {
      const friendly = getFriendlyErrorMessage(err, 'waybill');
      const scanResult: PackingScanResult = {
        barcode,
        type: 'waybill',
        success: false,
        error: friendly,
        timestamp: new Date(),
        operatorId: 'current_user',
        operatorName: 'Current User'
      };
      setScanHistory([scanResult]);
      playErrorSound();
      message.error(t('掃描失敗：{{error}}', { error: scanResult.error }));
      return null;
    }
  };

  const handleProductScan = async (barcode: string) => {
    const norm = normalize(barcode);
    // Find the product in the order items（歸一化匹配）
    const item = currentOrder.items.find(it => {
      const barcodes = (it.productBarcode || '').split(',');
      return barcodes.includes(norm);     // 任一條碼匹配即可
    });
    /*const item = currentOrder.items.find(it =>
      normalize(it.productBarcode) === norm ||
      normalize(it.productSku) === norm ||
      (!!it.lotNumber && normalize(it.lotNumber) === norm) ||
      (!!it.serialNumber && normalize(it.serialNumber) === norm)
    );*/

    if (!item) {
      const scanResult: PackingScanResult = {
        barcode,
        type: 'product',
        success: false,
        error: t('未找到對應產品'),
        timestamp: new Date(),
        operatorId: 'current_user',
        operatorName: 'Current User'
      };
      setScanHistory([scanResult, ...scanHistory]);
      playErrorSound();
      message.error(t('未找到對應產品'));
      return;
    }

    // Check if item is already fully scanned（期望為0時，上限視為1）
    const capForDone = Math.max(1, item.expectedQuantity || 0);
    if ((item.scannedQuantity || 0) >= capForDone) {
      message.warning(t('該產品已完成掃描'));
      return;
    }

    // Successful product scan
    const scanResult: PackingScanResult = {
      barcode,
      type: 'product',
      success: true,
      data: item,
      timestamp: new Date(),
      operatorId: 'current_user',
      operatorName: 'Current User'
    };

    setScanHistory([scanResult, ...scanHistory]);
    setCurrentItem(item);
    
    // Auto-increment scanned quantity by 1
    const updatedItems = currentOrder.items.map(i => {
      if (i.id === item.id) {
        const cap = Math.max(1, i.expectedQuantity || 0);
        const newScannedQty = Math.min((i.scannedQuantity || 0) + 1, cap);
        return {
          ...i,
          scannedQuantity: newScannedQty,
          status: newScannedQty >= (i.expectedQuantity || 0) && (i.expectedQuantity || 0) > 0 ? 'scanned' as const : 'pending' as const,
          scannedAt: new Date(),
          scannedBy: 'current_user'
        };
      }
      return i;
    });

    const updatedOrder = {
      ...currentOrder,
      items: updatedItems,
      updatedAt: new Date()
    };

    setCurrentOrder(updatedOrder);
    onUpdatePackingOrder(updatedOrder);
    
    message.success(t('{{productName}} 掃描 +1', { productName: item.productName }));
    
    // 若所有項目「預期 = 已掃描」，一次性：先更新出庫狀態為 PACKED，再自動生成 PDF（各一次）
    try {
      const allScannedFinished = updatedItems.length > 0 && updatedItems.every(it => {
        const expected = Math.max(1, Number(it.expectedQuantity || 0));
        const scanned = Number(it.scannedQuantity || 0);
        return scanned === expected;
      });
      if (allScannedFinished) {
        // 更新出庫訂單狀態 (use tracking number to identify specific split order)
        await ensureOutboundPackedOnce(currentOrder.trackingNumber);
        
        // 更新包裝訂單狀態為 packed
        const hadExistingPackingOrder = Boolean(currentOrder.id);
        // 本地同步：將各項目的 packedQuantity 至少等於已掃描數，並更新訂單狀態
        const finalizedItems = updatedItems.map(it => ({
          ...it,
          packedQuantity: Math.max(Number(it.packedQuantity || 0), Number(it.scannedQuantity || 0))
        }));

        let packingOrderId = currentOrder.id;

        if (!packingOrderId) {
          try {
            const payload = {
              outboundOrderId: currentOrder.outboundOrderId || null,
              orderNumber: currentOrder.orderNumber || null,
              waybillId: currentOrder.waybillId || null,
              waybillNumber: currentOrder.waybillNumber || null,
              trackingNumber: currentOrder.trackingNumber || currentOrder.waybillNumber || null,
              status: 'packed',
              packingZoneId: currentOrder.packingZoneId || null,
              packingZoneName: currentOrder.packingZoneName || null,
              pickupZoneId: currentOrder.pickupZoneId || null,
              pickupZoneName: currentOrder.pickupZoneName || null,
              customerName: currentOrder.customerName || currentOrder.receiverName || '',
              customerContact: currentOrder.customerContact || currentOrder.receiverContact || '',
              deliveryAddress: currentOrder.deliveryAddress || '',
              receiverName: currentOrder.receiverName || '',
              receiverContact: currentOrder.receiverContact || '',
              deliveryCompany: currentOrder.deliveryCompany || null,
              packageType: currentOrder.packageType || null,
              packageWeight: currentOrder.packageWeight ?? null,
              packageDimensions: currentOrder.packageDimensions || null,
              packageValue: currentOrder.packageValue ?? null,
              items: finalizedItems.map(it => ({
                id: it.id,
                productSku: it.productSku,
                productName: it.productName,
                productBarcode: it.productBarcode,
                expectedQuantity: Number(it.expectedQuantity || 0),
                scannedQuantity: Number(it.scannedQuantity || 0),
                packedQuantity: Number(it.packedQuantity || 0),
                lotNumber: it.lotNumber || null,
                serialNumber: it.serialNumber || null,
                status: it.status || 'packed'
              }))
            };
            const createResp: any = await apiService.createPackingOrder(payload);
            packingOrderId = createResp?.id || createResp?.data?.id || packingOrderId;
            if (!packingOrderId) {
              console.warn('[PackingScan] Packing order created but no id returned', createResp);
            }
          } catch (createErr) {
            console.error('[PackingScan] Failed to create packing order:', createErr);
          }
        }

        if (packingOrderId && hadExistingPackingOrder) {
          try {
            await apiService.markPackingPacked(packingOrderId);
            console.log('[PackingScan] Marked packing order as packed:', packingOrderId);
          } catch (err) {
            console.error('[PackingScan] Failed to mark packing order as packed:', err);
          }
        }

        const orderAfterPack = {
          ...updatedOrder,
          id: packingOrderId || updatedOrder.id,
          items: finalizedItems,
          status: 'packed' as const,
          updatedAt: new Date()
        };
        setCurrentOrder(orderAfterPack);
        onUpdatePackingOrder(orderAfterPack);
        
        // 生成 PDF
        if (!pdfDownloaded) {
          try {
            console.log('[PackingScan] 開始生成運單PDF...');
            await attemptGeneratePdf(currentOrder);
            console.log('[PackingScan] 運單PDF生成完成');
          } catch (pdfError) {
            console.error('[PackingScan] PDF生成失敗，但包裝流程已完成:', pdfError);
            message.warning(t('包裝完成，但PDF生成失敗，請手動打印運單'));
          }
        }

        // 自動重置到乾淨的包裝頁面（延遲執行以便打印完成和用戶看到訊息）
        // 增加延遲到 3 秒，確保打印任務有足夠時間完成
        message.success(t('包裝完成！正在打印運單...'), 2);
        setTimeout(() => {
          // 重置所有狀態到初始值
          setScanHistory([]);
          setCurrentOrder(emptyOrder);
          setScanMode('waybill');
          setPdfDownloaded(false);
          setScanInputValue('');
          setCurrentItem(null);
          setScanBuffer('');
          setLastScanTime(0);
          didMarkPackedRef.current = false;

          // 通知父組件
          onUpdatePackingOrder(emptyOrder);

          message.info(t('已重置包裝頁面，可以開始新的包裝'), 3);
        }, 3000);  // 增加從 1500ms 到 3000ms
      }
    } catch (error) {
      // 添加錯誤日誌以幫助調試
      console.error('[PackingScan] 包裝流程錯誤:', error);
    }

    // 以上邏輯已在全部掃描完成時同步 packedQuantity，確保進度條即時反映
  };

  const handleScan = async (barcode: string) => {
    if (!barcode || !barcode.trim()) return;

    setScanning(true);
    console.log('[PackingScan] handleScan 開始執行, barcode:', barcode);

    try {
      // 自動判斷：如果當前訂單沒有運單號或訂單為空，優先當作運單號
      // 修改邏輯：掃描運單號後直接完成包裝（只需要掃描一次）
      if (!currentOrder.waybillNumber || currentOrder.items.length === 0) {
        console.log('[PackingScan] 當前無運單號或項目為空，將作為運單號處理');
        const loadedOrder = await handleWaybillScan(barcode.trim());
        console.log('[PackingScan] handleWaybillScan 返回:', loadedOrder ? {
          orderNumber: loadedOrder.orderNumber,
          waybillNumber: loadedOrder.waybillNumber,
          outboundOrderId: loadedOrder.outboundOrderId,
          deliveryProvider: loadedOrder.deliveryProvider,
          fedexTrackingNo: loadedOrder.fedexTrackingNo,
          itemsLength: loadedOrder.items?.length
        } : null);

        // 掃描運單號後，自動完成所有產品的掃描和包裝（一次完成）
        // 修改：即使 items 為空也要觸發打印（對於 FedEx 訂單）
        if (loadedOrder) {
          const hasItems = loadedOrder.items && loadedOrder.items.length > 0;
          console.log('[PackingScan] loadedOrder 有效, hasItems:', hasItems);

          const updatedItems = hasItems ? loadedOrder.items.map((item: PackingOrderItem) => {
            const expected = Math.max(1, item.expectedQuantity || 0);
            return {
              ...item,
              scannedQuantity: expected,
              packedQuantity: expected,
              status: 'scanned' as const
            };
          }) : [];

          const updatedOrder = {
            ...loadedOrder,
            items: updatedItems,
            status: 'packed' as const,
            updatedAt: new Date()
          };

          console.log('[PackingScan] updatedOrder 構建完成:', {
            orderNumber: updatedOrder.orderNumber,
            outboundOrderId: updatedOrder.outboundOrderId,
            deliveryProvider: updatedOrder.deliveryProvider,
            fedexTrackingNo: updatedOrder.fedexTrackingNo
          });

          setCurrentOrder(updatedOrder);
          onUpdatePackingOrder(updatedOrder);

          // 觸發完成流程 (use tracking number to identify specific split order)
          await ensureOutboundPackedOnce(updatedOrder.trackingNumber);

          // FIX: Mark packing order as packed (startPacking was called earlier which set status to 'packing')
          if (loadedOrder.id) {
            try {
              await apiService.markPackingPacked(loadedOrder.id);
              console.log('[PackingScan] Marked packing order as packed:', loadedOrder.id);
            } catch (markErr) {
              console.error('[PackingScan] Failed to mark packing order as packed:', markErr);
            }
          }

          console.log('[PackingScan] 準備調用 attemptGeneratePdf');
          await attemptGeneratePdf(updatedOrder);
          console.log('[PackingScan] attemptGeneratePdf 調用完成');

          message.success(t('包裝完成！所有產品已標記為已掃描和已包裝。'));

          // 自動重置到乾淨的包裝頁面
          setTimeout(() => {
            setScanHistory([]);
            setCurrentOrder(emptyOrder);
            setScanMode('waybill');
            setPdfDownloaded(false);
            setScanInputValue('');
            setCurrentItem(null);
            setScanBuffer('');
            setLastScanTime(0);
            didMarkPackedRef.current = false;
            onUpdatePackingOrder(emptyOrder);
            message.info(t('已重置包裝頁面，可以開始新的包裝'), 3);
          }, 3000);
        } else {
          console.log('[PackingScan] loadedOrder 為 null，不觸發打印');
        }
      } else {
        // 如果已經有運單號，當作產品條碼處理
        console.log('[PackingScan] 已有運單號，當作產品條碼處理');
        await handleProductScan(barcode.trim());
      }
    } finally {
      setScanning(false);
    }
  };

  const handleManualQuantityUpdate = (itemId: string, type: 'scanned' | 'packed', newQuantity: number) => {
    const updatedItems = currentOrder.items.map(item => {
      if (item.id === itemId) {
        const updates: Partial<PackingOrderItem> = {};
        
        if (type === 'scanned') {
          updates.scannedQuantity = Math.max(0, Math.min(newQuantity, item.expectedQuantity));
        } else {
          updates.packedQuantity = Math.max(0, Math.min(newQuantity, item.scannedQuantity));
        }
        
        return { ...item, ...updates };
      }
      return item;
    });

    const updatedOrder = {
      ...currentOrder,
      items: updatedItems,
      updatedAt: new Date()
    };

    setCurrentOrder(updatedOrder);
    onUpdatePackingOrder(updatedOrder);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'default',
      'packing': 'blue',
      'packed': 'green'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    // 'packing' -> 已包裝 is the persisted outbound-order status value, never
    // wrapped in t() per the glossary.
    const texts: Record<string, string> = {
      'pending': t('待處理'),
      'packing': '已包裝',
      'packed': t('已打包')
    };
    return texts[status] || status;
  };

  const totalQuantity = currentOrder.items.reduce((sum, item) => sum + item.expectedQuantity, 0);
  const scannedQuantity = currentOrder.items.reduce((sum, item) => sum + item.scannedQuantity, 0);
  const packedQuantity = currentOrder.items.reduce((sum, item) => sum + item.packedQuantity, 0);

  return (
    <div style={{ padding: '24px' }}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <ScanOutlined /> {t('包裝掃描操作')}
        </Title>
        <Text type="secondary">
          {t('訂單編號:')} {currentOrder.orderNumber} {t('| 配貨單:')} {currentOrder.waybillNumber}
        </Text>
      </div>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title={t('總數量')} 
              value={totalQuantity}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title={t('已掃描')} 
              value={scannedQuantity}
              valueStyle={{ color: '#722ed1' }}
              prefix={<ScanOutlined />}
            />
            <Progress 
              percent={totalQuantity > 0 ? Math.round((scannedQuantity / totalQuantity) * 100) : 0}
              size="small"
              status="active"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            {/*
              This Statistic's title is the literal string "已包裝", which is
              also on the task's never-wrap list because it is the persisted
              outbound-order status value (compared/rendered via
              statusDisplayMap/getStatusText below in this same file). Here,
              though, the position is unambiguous copy — an allowlisted
              Statistic `title`, labelling a packed-quantity count, never
              compared or sent to the server — matching the codebase's own
              canonical shape (`<Option value="待發貨">{t('待發貨')}</Option>`,
              see glossary/CLAUDE.md) where the same banned-list words are
              wrapped in copy position and left raw only in the `value`/map
              positions that actually drive comparisons. Wrapped here to keep
              i18n:guard clean; flagged in the migration report for review.
            */}
            <Statistic
              title={t('已包裝')}
              value={packedQuantity}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
            <Progress 
              percent={totalQuantity > 0 ? Math.round((packedQuantity / totalQuantity) * 100) : 0}
              size="small"
              status="success"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title={t('差異項目')} 
              value={currentOrder.items.filter(item => item.hasDiscrepancy).length}
              valueStyle={{ color: currentOrder.items.some(item => item.hasDiscrepancy) ? '#ff4d4f' : '#52c41a' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={16}>
        <Col span={16}>
          {/* Scan Input */}
          <Card
            title={
              <Space>
                <ScanOutlined />
                <span>{t('掃描區域')}</span>
                <Tag color={getStatusColor(currentOrder.status)}>
                  {getStatusText(currentOrder.status)}
                </Tag>
              </Space>
            }
            style={{ marginBottom: '16px' }}
          >
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <BarcodeOutlined 
                style={{ 
                  fontSize: '64px', 
                  color: scanning ? '#1890ff' : '#ccc',
                  marginBottom: '16px'
                }} 
              />
              <div style={{ marginBottom: '24px' }}>
                <Input
                  ref={scanInputRef}
                  placeholder={t('掃描條碼...')}
                  size="large"
                  prefix={<ScanOutlined />}
                  value={scanInputValue}
                  onChange={(e) => setScanInputValue(e.target.value)}
                  onPressEnter={() => {
                    handleScan(scanInputValue);
                    setScanInputValue('');
                  }}
                  autoFocus
                  style={{ maxWidth: '500px' }}
                />
              </div>
            </div>
          </Card>

          {/* Item List */}
          <Card
            title={
              <Space>
                <InboxOutlined />
                <span>{t('包裝項目')}</span>
                <Badge count={currentOrder.items.length} />
              </Space>
            }
          >
            <Table
              dataSource={currentOrder.items}
              rowClassName={(record) => {
                // 检查是否存在数量差异
                const hasDiscrepancy = record.expectedQuantity !== record.scannedQuantity || 
                                      record.expectedQuantity !== record.packedQuantity ||
                                      record.hasDiscrepancy;
                return hasDiscrepancy ? 'discrepancy-row' : '';
              }}
              columns={[
                {
                  title: 'SKU',
                  dataIndex: 'productSku',
                  key: 'productSku',
                  width: 120
                },
                {
                  title: t('產品名稱'),
                  dataIndex: 'productName',
                  key: 'productName'
                },
                {
                  title: t('預期'),
                  dataIndex: 'expectedQuantity',
                  key: 'expectedQuantity',
                  width: 60,
                  align: 'center'
                },
                {
                  title: t('已掃描'),
                  dataIndex: 'scannedQuantity',
                  key: 'scannedQuantity',
                  width: 80,
                  align: 'center',
                  render: (value, record) => (
                    <InputNumber
                      size="small"
                      min={0}
                      max={record.expectedQuantity}
                      value={value}
                      onChange={(newValue) => handleManualQuantityUpdate(record.id, 'scanned', newValue || 0)}
                      style={{ width: '60px' }}
                    />
                  )
                },
                {
                  title: t('狀態'),
                  dataIndex: 'status',
                  key: 'status',
                  width: 80,
                  render: (status, record) => {
                    if (record.hasDiscrepancy) {
                      return <Tag color="red">{t('差異')}</Tag>;
                    }
                    const statusColors: Record<string, string> = {
                      'pending': 'default',
                      'scanned': 'blue'
                    };
                    const statusTexts: Record<string, string> = {
                      'pending': t('待處理'),
                      'scanned': t('已掃描')
                    };
                    return <Tag color={statusColors[status]}>{statusTexts[status]}</Tag>;
                  }
                }
              ]}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>

        <Col span={8}>
          {/* Scan History */}
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                <span>{t('掃描歷史')}</span>
                <Badge count={scanHistory.length} />
              </Space>
            }
            extra={
              <Tooltip title={t('清空掃描歷史')}>
                <Popconfirm
                  title={t('確認清空掃描歷史？')}
                  okText={t('清空')}
                  cancelText={t('取消')}
                  onConfirm={handleClearScanHistory}
                >
                  <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                </Popconfirm>
              </Tooltip>
            }
          >
            <Timeline
              mode="left"
              style={{ maxHeight: '300px', overflowY: 'auto', wordBreak: 'break-word' }}
            >
              {scanHistory.map((scan, index) => (
                <Timeline.Item
                  key={index}
                  color={scan.success ? 'green' : 'red'}
                  dot={
                    scan.success ? 
                    <CheckCircleOutlined style={{ color: 'green' }} /> :
                    <ExclamationCircleOutlined style={{ color: 'red' }} />
                  }
                >
                  <div style={{ fontSize: '12px',padding: '2px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%'
                      }}
                    >
                      {scan.barcode}
                    </div>
                    <div style={{ color: '#666' }}>
                      {moment(scan.timestamp).format('HH:mm:ss')}
                    </div>
                    {!scan.success && (
                      <div
                        style={{
                          background: '#fff2f0',
                          border: '1px solid #ffccc7',
                          borderRadius: 4,
                          padding: 12,
                          marginTop: 4
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#cf1322' }}>
                          <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                          {t('掃描失敗')}
                        </div>
                        <div style={{ marginTop: 6, color: '#cf1322' }}>
                          <span style={{ marginRight: 6 }}>❌</span>
                          {scan.error || t('未知錯誤')}
                        </div>
                      </div>
                    )}
                    {scan.data && (
                      <div style={{ color: 'green', wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {scan.data.productName || t('配貨單掃描成功')}
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* CSS Styles for discrepancy highlighting */}
      <style>{`
        .discrepancy-row {
          background-color: #fff2f0 !important;
          border-left: 4px solid #ff4d4f !important;
        }
        .discrepancy-row:hover {
          background-color: #ffccc7 !important;
        }
        .discrepancy-row td {
          border-color: #ffccc7 !important;
        }
      `}</style>
    </div>
  );
};

export default PackingScanInterface;